import React from 'react';
import { Group, Rect, Text, Circle } from 'react-konva';
import { DanceFloorItem as DanceFloorItemType, Position, Size } from '../../types';

interface DanceFloorItemProps {
  id: string;
  item: DanceFloorItemType;
  position: Position;
  size: Size;
  rotation: number;
  isSelected: boolean;
  onDragStart: (e: any) => void;
  onDragEnd: (e: any) => void;
  onClick: (e: any) => void;
  onRotate: (id: string, rotation: number) => void;
}

const DanceFloorItem: React.FC<DanceFloorItemProps> = ({
  id,
  item,
  position,
  size,
  rotation,
  isSelected,
  onDragStart,
  onDragEnd,
  onClick,
  onRotate
}) => {
  const { label } = item;

  // Handle rotation with the rotation handle
  const handleRotateStart = (e: any) => {
    e.cancelBubble = true; // Prevent parent drag
  };
  
  const handleRotateMove = (e: any) => {
    e.cancelBubble = true; // Prevent parent drag
    
    // Calculate the center of the item using the stage's coordinates
    const stage = e.target.getStage();
    const pointerPos = stage.getPointerPosition();
    
    // Use getParent().getAbsolutePosition() to get the actual position
    // accounting for scale, rotation, and any transformations
    const groupNode = e.target.getParent();
    const groupAbsPos = groupNode.getAbsolutePosition();
    
    // Calculate angle between center of item and current mouse position
    const deltaX = pointerPos.x - groupAbsPos.x;
    const deltaY = pointerPos.y - groupAbsPos.y;
    
    // Calculate angle in degrees and add 90° to make rotation start from the top
    let angle = Math.atan2(deltaY, deltaX) * 180 / Math.PI + 90;
    
    // Normalize angle to 0-360 range
    if (angle < 0) angle += 360;
    
    // Round to nearest 15 degrees for better usability
    const snappedAngle = Math.round(angle / 15) * 15;
    
    onRotate(id, snappedAngle);
  };

  return (
    <Group
      id={id}
      x={position.x}
      y={position.y}
      rotation={rotation}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onClick={onClick}
    >
      {/* Dance floor base */}
      <Rect
        id={id}
        width={size.width}
        height={size.height}
        fill="#c7b8e0" // Light purple/lavender color for dance floor
        stroke={isSelected ? "#3498db" : "#9a86c4"}
        strokeWidth={isSelected ? 2 : 1}
        cornerRadius={3}
        x={-size.width / 2}
        y={-size.height / 2}
      />

      {/* Dance floor pattern */}
      <Rect
        id={id}
        width={size.width - 6}
        height={size.height - 6}
        fill="#d9ccea" // Lighter color for pattern
        stroke="#b8a5d9"
        strokeWidth={0.5}
        cornerRadius={2}
        x={-(size.width - 6) / 2}
        y={-(size.height - 6) / 2}
      />

      {/* Dance floor pattern lines - horizontal */}
      {Array.from({ length: Math.ceil(size.height / 20) }, (_, i) => (
        <Rect
          key={`h-line-${i}`}
          width={size.width - 12}
          height={1}
          fill="#b8a5d9"
          x={-(size.width - 12) / 2}
          y={-(size.height / 2) + 10 + i * 20}
        />
      ))}

      {/* Dance floor pattern lines - vertical */}
      {Array.from({ length: Math.ceil(size.width / 20) }, (_, i) => (
        <Rect
          key={`v-line-${i}`}
          width={1}
          height={size.height - 12}
          fill="#b8a5d9"
          x={-(size.width / 2) + 10 + i * 20}
          y={-(size.height - 12) / 2}
        />
      ))}

      {/* Label */}
      {label && (
        <Text
          text={label}
          fontSize={14}
          fill="#000"
          fontStyle="bold"
          align="center"
          verticalAlign="middle"
          width={size.width * 0.9}
          height={size.height * 0.9}
          offsetX={size.width * 0.45}
          offsetY={size.height * 0.45}
        />
      )}

      {/* Rotation Handle - only show when selected */}
      {isSelected && (
        <>
          {/* Rotation indicator line */}
          <Rect
            x={0}
            y={-size.height / 2 - 30}
            width={2}
            height={30}
            fill="#3498db"
            offsetX={1}
          />
          
          {/* Rotation handle */}
          <Circle
            x={0}
            y={-size.height / 2 - 40}
            radius={10}
            fill="#3498db"
            stroke="#fff"
            strokeWidth={2}
            draggable
            onDragStart={handleRotateStart}
            onDragMove={handleRotateMove}
            onMouseEnter={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'grab';
            }}
            onMouseLeave={(e) => {
              const container = e.target.getStage()?.container();
              if (container) container.style.cursor = 'default';
            }}
          />
        </>
      )}
    </Group>
  );
};

export default DanceFloorItem; 